import { MINUTA_TEMPLATE } from "../constants/minutaTemplate";

/**
 * A lightweight markdown parser for formatting the AI-generated summary.
 */
export function parseMarkdown(md: string): string {
  if (!md) return "";
  const lines = md.split("\n");
  let inList = false;
  const htmlLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed.startsWith("### ")) {
      if (inList) {
        htmlLines.push("</ul>");
        inList = false;
      }
      htmlLines.push(
        `<h4 style="font-size: 14px; font-weight:600; color:var(--accent); margin:18px 0 8px; text-align: left;">${trimmed.substring(
          4
        )}</h4>`
      );
    } else if (trimmed.startsWith("## ")) {
      if (inList) {
        htmlLines.push("</ul>");
        inList = false;
      }
      htmlLines.push(
        `<h3 style="font-size: 16px; font-weight:600; color:var(--text); border-bottom:1px solid var(--border); padding-bottom:6px; margin:24px 0 12px; text-align: left; display:flex; align-items:center; gap:8px;">${trimmed.substring(
          3
        )}</h3>`
      );
    } else if (trimmed.startsWith("# ")) {
      if (inList) {
        htmlLines.push("</ul>");
        inList = false;
      }
      htmlLines.push(
        `<h2 style="font-size: 19px; font-weight:700; color:var(--text); margin:28px 0 14px; text-align: left;">${trimmed.substring(
          2
        )}</h2>`
      );
    } else if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      if (!inList) {
        htmlLines.push('<ul style="margin-bottom: 14px; text-align: left;">');
        inList = true;
      }
      let content = trimmed.substring(2);
      content = content.replace(
        /\*\*(.*?)\*\*/g,
        '<strong style="color:var(--text); font-weight:600;">$1</strong>'
      );
      content = content.replace(
        /\*(.*?)\*/g,
        '<em style="color:var(--text-muted);">$1</em>'
      );
      htmlLines.push(
        `<li style="margin-left: 20px; margin-bottom: 6px; color:var(--text-muted); line-height: 1.5; list-style-type: disc;">${content}</li>`
      );
    } else if (/^\d+\.\s+/.test(trimmed)) {
      if (inList) {
        htmlLines.push("</ul>");
        inList = false;
      }
      let content = trimmed.replace(/^\d+\.\s+/, "");
      content = content.replace(
        /\*\*(.*?)\*\*/g,
        '<strong style="color:var(--text); font-weight:600;">$1</strong>'
      );
      content = content.replace(
        /\*(.*?)\*/g,
        '<em style="color:var(--text-muted);">$1</em>'
      );
      const matchNum = trimmed.match(/^\d+/);
      const numStr = matchNum ? matchNum[0] : "1";
      htmlLines.push(
        `<div style="margin-bottom: 8px; color:var(--text-muted); line-height: 1.5; padding-left: 10px; text-align: left;"><span style="color:var(--accent); font-weight:600; margin-right:6px;">${numStr}.</span> ${content}</div>`
      );
    } else if (trimmed === "") {
      if (inList) {
        htmlLines.push("</ul>");
        inList = false;
      }
      htmlLines.push('<div style="height: 10px;"></div>');
    } else {
      if (inList) {
        htmlLines.push("</ul>");
        inList = false;
      }
      let content = trimmed;
      content = content.replace(
        /\*\*(.*?)\*\*/g,
        '<strong style="color:var(--text); font-weight:600;">$1</strong>'
      );
      content = content.replace(
        /\*(.*?)\*/g,
        '<em style="color:var(--text-muted);">$1</em>'
      );
      htmlLines.push(
        `<p style="margin-bottom: 12px; color:var(--text-muted); line-height: 1.6; text-align: left;">${content}</p>`
      );
    }
  }

  if (inList) {
    htmlLines.push("</ul>");
  }
  return htmlLines.join("\n");
}

/**
 * Download the meeting summary in Markdown format.
 */
export function downloadMarkdown(summaryText: string): void {
  if (!summaryText) return;
  const blob = new Blob([summaryText], { type: "text/markdown;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  const now = new Date();
  const dateStr = now.toISOString().split("T")[0];
  const timeStr = now.toLocaleTimeString([], { hour12: false }).replace(/:/g, "-");

  link.href = url;
  link.setAttribute("download", `Kira_Minuta_${dateStr}_${timeStr}.md`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Download the meeting summary in HTML format (using the macOS styled template).
 */
export function downloadHtml(summaryText: string): void {
  if (!summaryText) return;

  const now = new Date();
  const dateStr = now.toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const timeStr = now.toLocaleTimeString([], { hour12: false }).replace(/:/g, "-");
  const dateFileStr = now.toISOString().split("T")[0];

  const title = "Minuta de Reunión — Kira";
  const contentHtml = parseMarkdown(summaryText);

  const finalHtml = MINUTA_TEMPLATE
    .replace(/\{\{TITLE\}\}/g, title)
    .replace(/\{\{DATE\}\}/g, dateStr)
    .replace(/\{\{CONTENT\}\}/g, contentHtml);

  const blob = new Blob([finalHtml], { type: "text/html;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", `Kira_Minuta_${dateFileStr}_${timeStr}.html`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadChatTxt(transcript: any[]): void {
  if (!transcript || transcript.length === 0) return;
  const content = transcript.map(t => `[${t.timestamp}] ${t.orig} -> ${t.tran}`).join("\n");
  const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const dateStr = new Date().toISOString().split("T")[0];
  
  link.href = url;
  link.setAttribute("download", `Kira_Chat_${dateStr}.txt`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadChatJson(transcript: any[]): void {
  if (!transcript || transcript.length === 0) return;
  const content = JSON.stringify(transcript, null, 2);
  const blob = new Blob([content], { type: "application/json;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const dateStr = new Date().toISOString().split("T")[0];
  
  link.href = url;
  link.setAttribute("download", `Kira_Chat_${dateStr}.json`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadChatHtml(transcript: any[]): void {
  if (!transcript || transcript.length === 0) return;
  const listItems = transcript.map(t => 
    `<div style="margin-bottom: 12px; font-family: sans-serif;">
      <div style="font-size: 12px; color: #666;">[${t.timestamp}]</div>
      <div style="font-weight: bold; color: #333;">${t.orig}</div>
      <div style="font-style: italic; color: #007aff; margin-top: 4px;">${t.tran}</div>
    </div>`
  ).join("\n");
  const content = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Kira Chat Transcript</title></head><body style="padding: 20px; background: #fafafa;"><div style="max-width: 800px; margin: 0 auto; background: #fff; padding: 20px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">${listItems}</div></body></html>`;
  const blob = new Blob([content], { type: "text/html;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const dateStr = new Date().toISOString().split("T")[0];
  
  link.href = url;
  link.setAttribute("download", `Kira_Chat_${dateStr}.html`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
